// applyChangesToMenu.ts
// Pure function — takes existing menuData + approved changes, returns new menuData.
// No side effects, no network calls. Safe to call client-side before confirming.

export type ChangeType =
  | 'ADD_MENU'
  | 'ADD_CATEGORY'
  | 'ADD_ITEM'
  | 'ADD_HIDDEN_ITEM'
  | 'UPDATE_ITEM'
  | 'UPDATE_USER_CHOICES'
  | 'MOVE_ITEM'
  | 'MOVE_CATEGORY'
  | 'REMOVE_ITEM'
  | 'REMOVE_CATEGORY'
  | 'REORDER';

export interface MenuChange {
  id: string;
  type: ChangeType;
  approved: boolean;
  [key: string]: any;
}

function generateHexId(): string {
  return Math.floor(Math.random() * 16777215)
    .toString(16)
    .toUpperCase()
    .padStart(6, '0');
}

const DEFAULT_TIMINGS = {
  monday:    [{ start: '00:00', end: '23:59' }],
  tuesday:   [{ start: '00:00', end: '23:59' }],
  wednesday: [{ start: '00:00', end: '23:59' }],
  thursday:  [{ start: '00:00', end: '23:59' }],
  friday:    [{ start: '00:00', end: '23:59' }],
  saturday:  [{ start: '00:00', end: '23:59' }],
  sunday:    [{ start: '00:00', end: '23:59' }],
};

/**
 * Applies a list of approved changes to menuData.
 * Only changes with approved === true are applied.
 * Changes are applied in dependency order: menus → categories → items.
 * Returns a deep-cloned new menuData object — the original is never mutated.
 */
export function applyChangesToMenu(menuData: any, changes: MenuChange[]): any {
  // Deep clone so we never mutate the original
  const result = JSON.parse(JSON.stringify(menuData));

  const approved = changes.filter(c => c.approved);

  // Apply in dependency order
  const order: ChangeType[] = [
    'ADD_MENU',
    'ADD_CATEGORY',
    'MOVE_CATEGORY',
    'ADD_ITEM',
    'ADD_HIDDEN_ITEM',  // must come after ADD_MENU so Additional Options menu exists
    'UPDATE_ITEM',
    'UPDATE_USER_CHOICES',
    'MOVE_ITEM',
    'REORDER',
    'REMOVE_ITEM',
    'REMOVE_CATEGORY',
  ];

  const sorted = [...approved].sort(
    (a, b) => order.indexOf(a.type) - order.indexOf(b.type)
  );

  for (const change of sorted) {
    applyOne(result, change);
  }

  return result;
}

function applyOne(menu: any, change: MenuChange): void {
  switch (change.type) {
    case 'ADD_MENU':           return applyAddMenu(menu, change);
    case 'ADD_CATEGORY':       return applyAddCategory(menu, change);
    case 'ADD_ITEM':           return applyAddItem(menu, change);
    case 'ADD_HIDDEN_ITEM':    return applyAddHiddenItem(menu, change);
    case 'UPDATE_ITEM':        return applyUpdateItem(menu, change);
    case 'UPDATE_USER_CHOICES': return applyUpdateUserChoices(menu, change);
    case 'MOVE_ITEM':          return applyMoveItem(menu, change);
    case 'MOVE_CATEGORY':      return applyMoveCategory(menu, change);
    case 'REMOVE_ITEM':        return applyRemoveItem(menu, change);
    case 'REMOVE_CATEGORY':    return applyRemoveCategory(menu, change);
    case 'REORDER':            return applyReorder(menu, change);
  }
}

// ---------------------------------------------------------------------------
// Individual appliers
// ---------------------------------------------------------------------------

function applyAddMenu(menu: any, change: MenuChange): void {
  const exists = menu.menuSettings?.some((ms: any) => ms.menuName === change.menuName);
  if (exists) return;

  menu.menuSettings = menu.menuSettings || [];
  menu.menuSettings.push({
    menuName:   change.menuName,
    enabled:    change.enabled ?? true,
    timings:    DEFAULT_TIMINGS,
    categories: [],
  });
}

function applyAddCategory(menu: any, change: MenuChange): void {
  const ms = menu.menuSettings?.find((m: any) => m.menuName === change.menuName);
  if (!ms) return;

  const exists = ms.categories?.some((c: any) => c.id === change.categoryId);
  if (exists) return;

  ms.categories = ms.categories || [];
  ms.categories.push({
    id:    change.categoryId || generateHexId(),
    name:  change.categoryName,
    order: change.order ?? (ms.categories.length + 1),
  });
}

function applyAddItem(menu: any, change: MenuChange): void {
  // Resolve categoryId from categoryName + menuName if not already set
  const item = { ...change.item };

  if (!item.category) {
    const ms = menu.menuSettings?.find((m: any) => m.menuName === change.menuName);
    const cat = ms?.categories?.find((c: any) => c.name === change.categoryName);
    if (cat) item.category = cat.id;
  }

  if (!item.productId) item.productId = generateHexId();
  if (!item.menus || item.menus.length === 0) item.menus = [change.menuName];

  // Ensure all required fields have defaults
  item.isDisabled         = item.isDisabled  ?? false;
  item.isDeleted          = item.isDeleted   ?? false;
  item.outOfStock         = item.outOfStock  ?? false;
  item.frequentOptions    = item.frequentOptions    ?? [];
  item.additionalOptions  = item.additionalOptions  ?? [];
  item.userChoices        = item.userChoices        ?? [];
  item.productThumbnailUrl = item.productThumbnailUrl ?? '';

  menu.menu = menu.menu || [];
  menu.menu.push(item);
}

function applyUpdateItem(menu: any, change: MenuChange): void {
  const item = menu.menu?.find((i: any) => i.productId === change.productId);
  if (!item) return;

  // Only apply the fields listed in change.after
  Object.assign(item, change.after);
}

function applyMoveItem(menu: any, change: MenuChange): void {
  const item = menu.menu?.find((i: any) => i.productId === change.productId);
  if (!item) return;

  // Resolve new category ID from name + menu
  const ms = menu.menuSettings?.find((m: any) => m.menuName === change.toMenu);
  const cat = ms?.categories?.find((c: any) => c.name === change.toCategory);

  item.menus    = [change.toMenu];
  item.category = cat?.id ?? item.category;
}

function applyMoveCategory(menu: any, change: MenuChange): void {
  // Find the category in its source menu and remove it
  const fromMs = menu.menuSettings?.find((m: any) => m.menuName === change.fromMenu);
  const toMs   = menu.menuSettings?.find((m: any) => m.menuName === change.toMenu);
  if (!fromMs || !toMs) return;

  const catIdx = fromMs.categories?.findIndex((c: any) => c.id === change.categoryId);
  if (catIdx === undefined || catIdx === -1) return;

  const [cat] = fromMs.categories.splice(catIdx, 1);

  // Add it to the destination menu
  toMs.categories = toMs.categories || [];
  toMs.categories.push({ ...cat, order: toMs.categories.length + 1 });

  // Update all items in this category to point at the new menu
  menu.menu?.forEach((item: any) => {
    if (item.category === change.categoryId) {
      item.menus = [change.toMenu];
    }
  });
}

function applyRemoveItem(menu: any, change: MenuChange): void {
  const item = menu.menu?.find((i: any) => i.productId === change.productId);
  if (!item) return;
  // Soft delete — keeps the record but hides it from the app
  item.isDeleted = true;
}

function applyRemoveCategory(menu: any, change: MenuChange): void {
  const ms = menu.menuSettings?.find((m: any) => m.menuName === change.menuName);
  if (!ms) return;

  ms.categories = ms.categories?.filter((c: any) => c.id !== change.categoryId) ?? [];

  // Soft-delete all items in the removed category
  menu.menu?.forEach((item: any) => {
    if (item.category === change.categoryId) {
      item.isDeleted = true;
    }
  });
}

function applyReorder(menu: any, change: MenuChange): void {
  if (change.scope === 'categories') {
    const ms = menu.menuSettings?.find((m: any) => m.menuName === change.menuName);
    if (!ms) return;

    // newOrder is an array of categoryIds in the desired order
    const idxMap = new Map<string, number>(
      (change.newOrder as string[]).map((id, i) => [id, i])
    );
    ms.categories?.sort((a: any, b: any) => {
      const ai = idxMap.get(a.id) ?? 999;
      const bi = idxMap.get(b.id) ?? 999;
      return ai - bi;
    });
    ms.categories?.forEach((c: any, i: number) => { c.order = i + 1; });

  } else if (change.scope === 'items') {
    // newOrder is an array of productIds in the desired order
    const idxMap = new Map<string, number>(
      (change.newOrder as string[]).map((id, i) => [id, i])
    );
    menu.menu?.forEach((item: any) => {
      if (
        item.menus?.[0] === change.menuName &&
        (!change.categoryName || item.category === change.categoryName)
      ) {
        const newIdx = idxMap.get(item.productId);
        if (newIdx !== undefined) item.order = newIdx + 1;
      }
    });
  }
}

function applyUpdateUserChoices(menu: any, change: MenuChange): void {
  const item = menu.menu?.find((i: any) => i.productId === change.productId);
  if (!item) return;

  // Replace the entire userChoices array with the new one.
  // Each entry must conform to the userChoices schema:
  // { name, type, limit, optionRequired, choices, choicesMetadata }
  item.userChoices = (change.after || []).map((c: any) => ({
    name:            c.name,
    type:            c.type,
    limit:           c.limit ?? null,
    optionRequired:  c.optionRequired ?? false,
    choices:         c.choices || [],
    choicesMetadata: c.choicesMetadata?.length
      ? c.choicesMetadata
      : (c.choices || []).map((v: string) => ({ value: v, taxExempt: false })),
  }));
}

function applyAddHiddenItem(menu: any, change: MenuChange): void {
  const HIDDEN_MENU = 'Additional Options';

  // 1. Ensure the Additional Options menu entry exists in menuSettings
  let hiddenMs = menu.menuSettings?.find((ms: any) => ms.menuName === HIDDEN_MENU);
  if (!hiddenMs) {
    menu.menuSettings = menu.menuSettings || [];
    hiddenMs = {
      menuName:   HIDDEN_MENU,
      enabled:    false,
      timings: {
        monday:    [{ start: '00:00', end: '23:59' }],
        tuesday:   [{ start: '00:00', end: '23:59' }],
        wednesday: [{ start: '00:00', end: '23:59' }],
        thursday:  [{ start: '00:00', end: '23:59' }],
        friday:    [{ start: '00:00', end: '23:59' }],
        saturday:  [{ start: '00:00', end: '23:59' }],
        sunday:    [{ start: '00:00', end: '23:59' }],
      },
      categories: [],
    };
    menu.menuSettings.push(hiddenMs);
  }

  // 2. Ensure the category exists inside Additional Options
  const categoryName = change.item?.categoryName || change.categoryName || 'Add-ons';
  let cat = hiddenMs.categories?.find((c: any) => c.name === categoryName);
  if (!cat) {
    const newCat = {
      id:    generateHexId(),
      name:  categoryName,
      order: (hiddenMs.categories?.length ?? 0) + 1,
    };
    hiddenMs.categories = hiddenMs.categories || [];
    hiddenMs.categories.push(newCat);
    cat = newCat;
  }

  // 3. Build the hidden item
  const newProductId = generateHexId();
  const item = { ...change.item };

  item.productId           = newProductId;
  item.menus               = [HIDDEN_MENU];
  item.category            = cat.id;
  item.isDisabled          = false;
  item.isDeleted           = false;
  item.outOfStock          = false;
  item.frequentOptions     = [];
  item.additionalOptions   = item.additionalOptions ?? [];
  item.productThumbnailUrl = '';
  item.userChoices         = (item.userChoices || []).map((c: any) => ({
    name:            c.name,
    type:            c.type,
    limit:           c.limit ?? null,
    optionRequired:  c.optionRequired ?? false,
    choices:         c.choices || [],
    choicesMetadata: c.choicesMetadata?.length
      ? c.choicesMetadata
      : (c.choices || []).map((v: string) => ({ value: v, taxExempt: false })),
  }));

  menu.menu = menu.menu || [];
  menu.menu.push(item);

  // 4. Wire the new productId into each linked item's additionalOptions array
  const linkTo: string[] = change.linkToProductIds || [];
  linkTo.forEach((targetId: string) => {
    const target = menu.menu.find((i: any) => i.productId === targetId);
    if (!target) return;
    target.additionalOptions = target.additionalOptions || [];
    if (!target.additionalOptions.includes(newProductId)) {
      target.additionalOptions.push(newProductId);
    }
  });
}